import { useAppKitProvider } from '@reown/appkit/react';
import { BrowserProvider, Eip1193Provider, ethers } from 'ethers';
import { FC, useCallback, useEffect, useState } from 'react';
import IconAGX from 'src/assets/images/aix-bot-icon.svg?react';
import IconSwap from 'src/assets/images/swap-icon.svg?react';
import { UIButton } from 'src/components/UI/UIButton.tsx';
import { assetTokenAbi, bondingAbi, routerAbi } from 'src/contracts/abi';
import { ADDRESSES } from 'src/contracts/addresses.ts';
import { useBalanceOf } from 'src/hooks/useBalanceOf.tsx';
import { useSetModal } from 'src/providers/ModalsProvider.tsx';
import { Agent } from 'src/types/agents.ts';
import { BN, getAtomicAmount, getDisplayAmount, numberWithSpaces } from 'src/utils/bigNumber.ts';

type SwapAction = 'buy' | 'sell';

interface SwapTokensProps {
  agent: Agent;
}

export const SwapTokens: FC<SwapTokensProps> = ({ agent }) => {
  const { walletProvider } = useAppKitProvider<Eip1193Provider>('eip155');
  const setModal = useSetModal();

  const [swapState, setSwapState] = useState({
    val1: '0',
    val2: '0',
    action: 'buy' as SwapAction,
  });

  const { val1, val2, action } = swapState;

  const token1 = useBalanceOf(ADDRESSES.assetToken);
  const token2 = useBalanceOf(agent.contract_address);

  const [contracts, setContracts] = useState<{
    router: ethers.Contract;
    bonding: ethers.Contract;
    assetToken: ethers.Contract;
    memeToken: ethers.Contract;
  } | null>(null);

  useEffect(() => {
    async function initContracts() {
      if (!walletProvider) return;
      const provider = new BrowserProvider(walletProvider);
      const signer = await provider.getSigner();

      setContracts({
        router: new ethers.Contract(ADDRESSES.router, routerAbi, signer),
        bonding: new ethers.Contract(ADDRESSES.bondingAddress, bondingAbi, signer),
        assetToken: new ethers.Contract(ADDRESSES.assetToken, assetTokenAbi, signer),
        memeToken: new ethers.Contract(agent.contract_address, assetTokenAbi, signer),
      });
    }

    initContracts();
  }, [walletProvider, agent.contract_address]);

  const calculateOutAmount = useCallback(
    async (actionIn?: 'buy' | 'sell') => {
      if (!contracts) return;

      const actionValue = actionIn || action;
      const inputAmount = getAtomicAmount(actionValue === 'buy' ? val1 : val2);

      try {
        const amount = await contracts.router.getAmountsOut(
          actionValue === 'buy' ? agent.contract_address : ADDRESSES.assetToken,
          actionValue === 'buy' ? ADDRESSES.assetToken : agent.contract_address,
          inputAmount,
        );

        setSwapState((prev) => ({
          ...prev,
          [actionValue === 'buy' ? 'val2' : 'val1']: getDisplayAmount(amount),
        }));
      } catch (err) {
        console.error('Error calculating amount:', err);
      }
    },
    [contracts, action, val1, val2, agent.contract_address],
  );

  useEffect(() => {
    calculateOutAmount();
  }, [val1, val2, calculateOutAmount]);

  const handleChangeDirection = () => {
    setSwapState((prev) => ({
      ...prev,
      action: prev.action === 'buy' ? 'sell' : 'buy',
    }));
    calculateOutAmount(action === 'buy' ? 'sell' : 'buy');
  };

  async function swap() {
    const routerAddress = ADDRESSES.router;
    const memeTokenAddress = agent.contract_address;

    try {
      if (!walletProvider) return;
      const provider = new BrowserProvider(walletProvider);
      const signer = await provider.getSigner();

      const bonding = new ethers.Contract(ADDRESSES.bondingAddress, bondingAbi, signer);
      const assetOutContract = new ethers.Contract(
        action === 'buy' ? ADDRESSES.assetToken : memeTokenAddress,
        assetTokenAbi,
        signer,
      );

      const value = getAtomicAmount(action === 'buy' ? val1 : val2);

      const currentAllowance = await assetOutContract.allowance(
        await signer.getAddress(),
        routerAddress,
      );

      if (BN(currentAllowance).lt(value)) {
        const approveTx = await assetOutContract.approve(routerAddress, value);

        console.log('approve tx hash:', approveTx.hash);

        setModal({
          modalKey: 'loader',
          title: 'Approving',
          txHash: approveTx.hash,
        });

        const approveRecp = await approveTx.wait();
        if (approveRecp.status === 1) {
          console.log('Approval successful');
        } else {
          setModal(null);
          throw new Error('Failed to approve');
        }
      }

      const tx = await bonding[action](value, memeTokenAddress, {
        gasLimit: 3000000,
      });

      setModal({
        modalKey: 'loader',
        title: 'Swapping',
        txHash: tx.hash,
      });

      const receipt = await tx.wait();

      if (receipt.status === 1) {
        console.log(`${action} successfully`);
        token1.refetch();
        token2.refetch();
      } else {
        throw new Error(`${action} transaction failed`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setModal(null);
    }
  }

  return (
    <div className="flex flex-col rounded-3xl bg-[#1A1C1E] p-6">
      <h2 className="mb-6 text-lg font-semibold">Swap</h2>
      <div
        className={`relative mb-2 flex gap-1 ${action === 'sell' ? 'flex-col-reverse' : 'flex-col'}`}
      >
        <div className="rounded-3xl border border-[#48494B] bg-[#1A1C1E] p-3">
          <div className="flex justify-between">
            <div className="flex max-w-[70%] flex-col">
              <input
                className="w-full bg-transparent text-2xl font-bold focus:outline-none"
                value={val1}
                onChange={(e) => setSwapState((prev) => ({ ...prev, val1: e.target.value }))}
              />
              {token1.isPending || token1.isRefetching ? (
                <span className="text-sm opacity-50">Loading balance...</span>
              ) : (
                <span
                  className="cursor-pointer text-sm opacity-50"
                  onClick={() =>
                    setSwapState((prev) => ({ ...prev, val1: token1.balance.fullPrecision }))
                  }
                >
                  Balance: {numberWithSpaces(token1.balance.formatted || '0')}
                </span>
              )}
            </div>
            <div className="flex max-w-[30%] items-center justify-center gap-[6px] rounded-2xl bg-[#303335] p-2">
              <div>
                <IconAGX className="h-6 w-6 rounded-full" />
              </div>
              <span className="font-regular inline-block overflow-hidden text-ellipsis whitespace-nowrap text-sm">
                AGX
              </span>
              {/*<Icon icon="fa6-solid:chevron-right" className="h-3 w-3 opacity-40" />*/}
            </div>
          </div>
        </div>
        <IconSwap
          onClick={handleChangeDirection}
          className="absolute left-1/2 top-1/2 h-[38px] w-[38px] -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-[#48494B]"
        />
        <div className="rounded-3xl border border-[#48494B] bg-[#1A1C1E] p-3">
          <div className="flex justify-between">
            <div className="flex max-w-[70%] flex-col">
              <input
                className="w-full bg-transparent text-2xl font-bold focus:outline-none"
                value={val2}
                onChange={(e) => setSwapState((prev) => ({ ...prev, val2: e.target.value }))}
              />
              {token2.isPending || token2.isRefetching ? (
                <span className="text-sm opacity-50">Loading balance...</span>
              ) : (
                <span
                  className="cursor-pointer text-sm opacity-50"
                  onClick={() =>
                    setSwapState((prev) => ({ ...prev, val2: token2.balance.fullPrecision }))
                  }
                >
                  Balance: {numberWithSpaces(token2.balance.formatted || '0')}
                </span>
              )}
            </div>
            <div className="flex max-w-[30%] items-center justify-center gap-[6px] rounded-2xl bg-[#303335] p-2">
              <img src={agent.image_url} className="h-6 w-6 rounded-full object-cover" />
              <span className="font-regular inline-block overflow-hidden text-ellipsis whitespace-nowrap text-sm">
                {agent.ticker}
              </span>
              {/*<Icon icon="fa6-solid:chevron-right" className="h-3 w-3 opacity-40" />*/}
            </div>
          </div>
        </div>
      </div>
      <div>
        <UIButton buttonColor="gray" className="w-full" size="xl" onClick={swap}>
          Swap
        </UIButton>
      </div>
    </div>
  );
};
